import { useParams } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import '../App.css';

import { API_BASE_URL, getToken } from '../dppApi';

// Public Digital Product Passport page. This is where a scanned product QR code
// lands (QR -> /api/passport/<id>/r -> 302 -> /jewellery/<id>).

// ISO 639-1 → display label for the toggle button
const LANG_LABELS = {
    fr: "Français", de: "Deutsch", it: "Italiano", es: "Español",
    pt: "Português", ja: "日本語", zh: "中文", ko: "한국어",
    ar: "العربية", hi: "हिन्दी", ru: "Русский", nl: "Nederlands",
    pl: "Polski", sv: "Svenska", tr: "Türkçe",
    // Indian regional languages
    ta: "தமிழ்", te: "తెలుగు", kn: "ಕನ್ನಡ", ml: "മലയാളം",
    bn: "বাংলা", gu: "ગુજરાતી", mr: "मराठी", pa: "ਪੰਜਾਬੀ",
    or: "ଓଡ଼ିଆ", as: "অসমীয়া", ne: "नेपाली", ur: "اردو",
    // Other
    uk: "Українська", cs: "Čeština", el: "Ελληνικά", ro: "Română",
    th: "ภาษาไทย", vi: "Tiếng Việt", id: "Bahasa Indonesia",
    ms: "Bahasa Melayu", da: "Dansk", fi: "Suomi", no: "Norsk",
    hu: "Magyar", sk: "Slovenčina", he: "עברית", fa: "فارسی", sw: "Kiswahili",
};

// Country code (ISO 3166-1 alpha-2) → primary language (ISO 639-1).
// India is intentionally absent — handled at state level by _indiaStateLang().
// English-speaking countries map to 'en' so the toggle is suppressed.
const COUNTRY_LANG = {
    FR: 'fr', MC: 'fr',
    DE: 'de', AT: 'de', LI: 'de', CH: 'de',
    IT: 'it', SM: 'it', VA: 'it',
    ES: 'es', MX: 'es', CO: 'es', AR: 'es', CL: 'es', PE: 'es', VE: 'es', EC: 'es',
    PT: 'pt', BR: 'pt', AO: 'pt', MZ: 'pt',
    RU: 'ru', BY: 'ru', KZ: 'ru',
    JP: 'ja', CN: 'zh', TW: 'zh', HK: 'zh',
    KR: 'ko', TH: 'th', VN: 'vi', ID: 'id', MY: 'ms',
    BD: 'bn', PK: 'ur', NP: 'ne',
    SA: 'ar', EG: 'ar', AE: 'ar', IQ: 'ar', SY: 'ar', JO: 'ar',
    LB: 'ar', LY: 'ar', TN: 'ar', DZ: 'ar', MA: 'ar', KW: 'ar', QA: 'ar',
    TR: 'tr', SE: 'sv', NO: 'no', DK: 'da', FI: 'fi',
    NL: 'nl', BE: 'nl', PL: 'pl', CZ: 'cs', SK: 'sk', HU: 'hu',
    RO: 'ro', BG: 'bg', GR: 'el', UA: 'uk', IL: 'he', IR: 'fa',
    US: 'en', GB: 'en', CA: 'en', AU: 'en', NZ: 'en', IE: 'en', SG: 'en', ZA: 'en',
};

// ipapi.co returns region = Indian state name. Substring match handles
// name variations ("NCT of Delhi", "Telangana State", etc.).
function _indiaStateLang(region) {
    const r = (region || '').toLowerCase();
    if (r.includes('andhra') || r.includes('telangana'))                   return 'te';
    if (r.includes('tamil'))                                               return 'ta';
    if (r.includes('karnataka'))                                           return 'kn';
    if (r.includes('kerala') || r.includes('lakshadweep'))                 return 'ml';
    if (r.includes('bengal') || r.includes('tripura'))                     return 'bn';
    if (r.includes('gujarat') || r.includes('dadra') || r.includes('daman')) return 'gu';
    if (r.includes('maharashtra') || r.includes('goa'))                    return 'mr';
    if (r.includes('punjab') || r.includes('chandigarh'))                  return 'pa';
    if (r.includes('odisha') || r.includes('orissa'))                      return 'or';
    if (r.includes('assam'))                                               return 'as';
    if (r.includes('sikkim'))                                              return 'ne';
    if (r.includes('jammu') || r.includes('kashmir'))                      return 'ur';
    // Delhi NCR, UP, Bihar, Rajasthan, MP, Haryana, Uttarakhand, HP,
    // Jharkhand, Chhattisgarh, Ladakh, Northeast states → Hindi
    return 'hi';
}

const Passport = () => {
    const { qrCodeId } = useParams();
    const [passport, setPassport] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showLocal, setShowLocal] = useState(false);
    const [localContent, setLocalContent] = useState(null);
    const [localLang, setLocalLang] = useState(null); // resolved from IP geolocation
    const [logoFailed, setLogoFailed] = useState(false);
    const [headerLogoFailed, setHeaderLogoFailed] = useState(false);
    const [activeSlide, setActiveSlide] = useState(0);
    const [isOwner, setIsOwner] = useState(false);
    const [isBrand, setIsBrand] = useState(false);
    const [privateDocs, setPrivateDocs] = useState([]);
    const [ownershipHistory, setOwnershipHistory] = useState([]);
    const slidesRef = useRef(null);

    useEffect(() => {
        fetch(`${API_BASE_URL}/api/passport/${qrCodeId}`)
            .then(res => {
                if (!res.ok) throw new Error(res.status === 404 ? "Passport not found" : "Failed to load passport");
                return res.json();
            })
            .then(data => { setPassport(data); setLocalContent(null); setShowLocal(false); setLogoFailed(false); setHeaderLogoFailed(false); })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [qrCodeId]);

    useEffect(() => {
        const token = getToken('user');
        if (!token || !qrCodeId) return;
        fetch(`${API_BASE_URL}/api/passport/${qrCodeId}/ownership`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(res => res.ok ? res.json() : { is_owner: false })
            .then(data => {
                setIsOwner(!!data.is_owner);
                setOwnershipHistory(data.ownership_history || []);
                if (data.is_owner) {
                    fetch(`${API_BASE_URL}/api/passport/${qrCodeId}/private-docs`, {
                        headers: { Authorization: `Bearer ${token}` },
                    })
                        .then(res => res.ok ? res.json() : [])
                        .then(setPrivateDocs)
                        .catch(() => {});
                }
            })
            .catch(() => {});
    }, [qrCodeId]);

    useEffect(() => {
        const token = getToken('brand');
        if (!token || !qrCodeId) return;
        // Treat the viewer as "this product's brand" only if the backend
        // authorizes them for it. /private-docs returns 200 for the
        // manufacturing brand and 403 for any other brand, so the response
        // status — not mere token presence — decides isBrand. This keeps a
        // logged-in brand from getting brand-only UI on another brand's passport.
        fetch(`${API_BASE_URL}/api/passport/${qrCodeId}/private-docs`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(res => {
                if (!res.ok) { setIsBrand(false); return []; }   // 403 => not this product's brand
                setIsBrand(true);
                return res.json();
            })
            .then(setPrivateDocs)
            .catch(() => { setIsBrand(false); });
    }, [qrCodeId]);

    // Resolve regional language from the viewer's physical location.
    // Primary: GPS coordinates → Nominatim reverse geocode (state-level for India).
    // Fallback: IP geolocation via ipapi.co (if GPS is denied or times out).
    useEffect(() => {
        function langFromCountryState(countryCode, state) {
            if (countryCode === 'IN') return _indiaStateLang(state);
            return COUNTRY_LANG[countryCode] || 'en';
        }

        function applyLang(lang) {
            setLocalLang(lang && lang !== 'en' ? lang : null);
        }

        function fromIP() {
            fetch('https://ipapi.co/json/')
                .then(r => r.json())
                .then(d => applyLang(langFromCountryState(d.country_code, d.region)))
                .catch(() => applyLang(null));
        }

        function fromCoords(lat, lon) {
            fetch(
                `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`,
                { headers: { 'Accept-Language': 'en-US' } }
            )
                .then(r => r.json())
                .then(d => {
                    const cc = (d.address?.country_code || '').toUpperCase();
                    const state = d.address?.state || d.address?.county || '';
                    applyLang(langFromCountryState(cc, state));
                })
                .catch(fromIP);
        }

        if ('geolocation' in navigator) {
            navigator.geolocation.getCurrentPosition(
                pos => fromCoords(pos.coords.latitude, pos.coords.longitude),
                () => fromIP(),
                { timeout: 8000, maximumAge: 600000 }
            );
        } else {
            fromIP();
        }
    }, []);

    useEffect(() => {
        const fontUrl = passport?.brand_font_url;
        if (!fontUrl) return;
        const isCdn = fontUrl.includes('fonts.googleapis.com') || fontUrl.includes('use.typekit.net');
        let href;
        if (isCdn) {
            href = fontUrl;
        } else {
            const params = new URLSearchParams({ url: fontUrl });
            if (passport.brand_font) params.set('family', passport.brand_font);
            href = `${API_BASE_URL}/api/font-proxy?${params}`;
        }
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        document.head.appendChild(link);
        return () => { if (document.head.contains(link)) document.head.removeChild(link); };
    }, [passport?.brand_font_url]);

    const productLang = passport?.detected_lang || 'en';
    // Show the toggle when either the viewer's regional language or the product's own
    // language is non-English. Viewer's location takes priority.
    const displayLang = localLang || (productLang !== 'en' ? productLang : null);

    useEffect(() => {
        if (!showLocal || !displayLang || localContent) return;
        // Product is already in the target language — use stored text directly.
        if (displayLang === productLang && productLang !== 'en') {
            setLocalContent({ name: passport.name, description: passport.description });
            return;
        }
        // Otherwise request a translation from the backend.
        fetch(`${API_BASE_URL}/api/passport/${qrCodeId}/translate?lang=${displayLang}`)
            .then(r => r.json())
            .then(d => setLocalContent({ name: d.name, description: d.description }))
            .catch(() => setLocalContent({ name: passport.name_en || passport.name, description: passport.description_en || passport.description }));
    }, [showLocal, displayLang, localContent, qrCodeId, passport, productLang]);

    const scrollToSlide = (i) => {
        const el = slidesRef.current;
        if (el) el.scrollTo({ left: i * el.offsetWidth, behavior: 'smooth' });
    };

    const onSlidesScroll = () => {
        const el = slidesRef.current;
        if (el) setActiveSlide(Math.round(el.scrollLeft / el.offsetWidth));
    };

    if (loading) return <div className="passport-page"><p className="passport-status">Loading passport…</p></div>;
    if (error) return <div className="passport-page"><p className="passport-status">{error}</p></div>;

    const { materials = {}, eu_compliance = {}, documents = [] } = passport;
    const receipts   = privateDocs.filter(d => d.doc_type === 'receipt');
    const appraisals = privateDocs.filter(d => d.doc_type === 'appraisal');
    const insurance  = privateDocs.filter(d => d.doc_type === 'insurance');
    const warranties = documents.filter(d => d.doc_type === 'warranty');
    const showImage = passport.image_url && /^https?:\/\//.test(passport.image_url);
    const localLabel = LANG_LABELS[displayLang] || displayLang?.toUpperCase();
    const displayName = showLocal ? (localContent?.name ?? passport.name) : (passport.name_en || passport.name);
    const displayDesc = showLocal ? (localContent?.description ?? passport.description) : (passport.description_en || passport.description);

    return (
        <div className="passport-page">
            <div className="passport-card">
                {/* Language toggle — temporarily disabled
                {displayLang && (
                    <div className="passport-lang-toggle">
                        <button
                            className={`lang-btn${!showLocal ? " active" : ""}`}
                            onClick={() => setShowLocal(false)}
                        >EN</button>
                        <button
                            className={`lang-btn${showLocal ? " active" : ""}`}
                            onClick={() => setShowLocal(true)}
                        >{localLabel}</button>
                    </div>
                )}
                */}
                <div className="passport-brand-header">
                    {passport.brand_header_logo_url && !headerLogoFailed ? (
                        <img
                            className="passport-brand-header-logo"
                            src={passport.brand_header_logo_url}
                            alt={passport.brand_title || passport.brand_name}
                            onError={() => setHeaderLogoFailed(true)}
                        />
                    ) : (
                        <span
                            style={{
                                color: passport.brand_text_color || passport.brand_color || undefined,
                                fontFamily: passport.brand_font ? `'${passport.brand_font}', sans-serif` : undefined,
                            }}
                        >
                            {passport.brand_title || passport.brand_name}
                        </span>
                    )}
                </div>

                <div className="passport-slides" ref={slidesRef} onScroll={onSlidesScroll}>
                    {/* Slide 1: Image + Description */}
                    <div className="passport-slide">
                        {showImage && <img className="passport-image" src={passport.image_url} alt={displayName} />}
                        <h1 className="passport-product-name">{displayName}</h1>
                        {displayDesc && (
                            <Section title="Description">
                                <p className="passport-description">{displayDesc}</p>
                            </Section>
                        )}
                    </div>

                    {/* Slide 2: Materials + Gemstones */}
                    <div className="passport-slide">
                        <Section title="Materials">
                            <Row label="Metal" value={materials.metal_type} />
                            <Row label="Purity" value={materials.metal_purity} />
                            <Row label="Weight (g)" value={materials.metal_weight_g} />
                        </Section>
                        {materials.gemstones && materials.gemstones.length > 0 && (
                            <Section title="Gemstones">
                                {materials.gemstones.map((g, i) => (
                                    <div className="passport-subblock" key={i}>
                                        <Row label="Type" value={g.stone_type} />
                                        <Row label="Origin" value={g.origin} />
                                        <Row label="Carat" value={g.carat} />
                                        <Row label="Cut" value={g.cut} />
                                        <Row label="Color" value={g.color} />
                                        <Row label="Clarity" value={g.clarity} />
                                    </div>
                                ))}
                            </Section>
                        )}
                        <Section title="Certifications">
                            {materials.gemstones && materials.gemstones.some(g => g.certification_body)
                                ? materials.gemstones.filter(g => g.certification_body).map((g, i) => (
                                    <div className="passport-subblock" key={i}>
                                        <Row label="Stone" value={g.stone_type} />
                                        <Row label="Body" value={g.certification_body} />
                                        <Row label="ID" value={g.certification_id} />
                                    </div>
                                ))
                                : null
                            }
                            {documents.filter(d => d.doc_type === 'certificate').map((d, i) => (
                                <div className="passport-subblock" key={`doc-${i}`}>
                                    <a
                                        href={d.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{ fontSize: 14, color: '#555' }}
                                    >
                                        {d.issued_by ? `Certificate — ${d.issued_by}` : 'Certificate'}
                                        {d.issued_date ? ` (${d.issued_date})` : ''}
                                    </a>
                                </div>
                            ))}
                        </Section>
                        <Section title="Warranty">
                            {warranties.map((d, i) => (
                                <div className="passport-subblock" key={`warranty-${i}`}>
                                    <a
                                        href={d.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{ fontSize: 14, color: '#555' }}
                                    >
                                        {d.issued_by ? `Warranty — ${d.issued_by}` : 'Warranty'}
                                        {d.issued_date ? ` (${d.issued_date})` : ''}
                                    </a>
                                </div>
                            ))}
                        </Section>
                        {(isOwner || (isBrand && receipts.length > 0)) && (
                            <Section title="Receipts">
                                {receipts.length === 0
                                    ? <p className="passport-description" style={{ color: '#bbb' }}>No receipts uploaded.</p>
                                    : receipts.map((r, i) => (
                                        <div className="passport-subblock" key={i}>
                                            <a href={r.url} target="_blank" rel="noreferrer" style={{ fontSize: 14, color: '#555' }}>
                                                {r.issued_by ? `Receipt — ${r.issued_by}` : 'Receipt'}
                                                {r.issued_date ? ` (${r.issued_date})` : ''}
                                            </a>
                                        </div>
                                    ))
                                }
                            </Section>
                        )}
                        {(isOwner || (isBrand && appraisals.length > 0)) && (
                            <Section title="Appraisals">
                                {appraisals.length === 0
                                    ? <p className="passport-description" style={{ color: '#bbb' }}>No appraisals uploaded.</p>
                                    : appraisals.map((d, i) => (
                                        <div className="passport-subblock" key={i}>
                                            <a href={d.url} target="_blank" rel="noreferrer" style={{ fontSize: 14, color: '#555' }}>
                                                {d.issued_by ? `Appraisal — ${d.issued_by}` : 'Appraisal'}
                                                {d.issued_date ? ` (${d.issued_date})` : ''}
                                            </a>
                                        </div>
                                    ))
                                }
                            </Section>
                        )}
                        {isOwner && (
                            <Section title="Insurance">
                                {insurance.length === 0
                                    ? <p className="passport-description" style={{ color: '#bbb' }}>No insurance uploaded.</p>
                                    : insurance.map((d, i) => (
                                        <div className="passport-subblock" key={i}>
                                            <a href={d.url} target="_blank" rel="noreferrer" style={{ fontSize: 14, color: '#555' }}>
                                                {d.issued_by ? `Insurance — ${d.issued_by}` : 'Insurance'}
                                                {d.issued_date ? ` (${d.issued_date})` : ''}
                                            </a>
                                        </div>
                                    ))
                                }
                            </Section>
                        )}
                    </div>

                    {/* Slide 3: Compliance / Mine / Journey / Care */}
                    <div className="passport-slide">
                        <Section title="Compliance">
                            <RowAll label="Country of origin" value={eu_compliance.country_of_origin} />
                            <RowAll label="Carbon footprint" value={eu_compliance.carbon_footprint_kg} />
                            <RowAll label="RJC certified" value={fmtBool(eu_compliance.rjc_certified)} />
                        </Section>

                        <Section title="Care Information">
                            <p className="passport-description">{eu_compliance.care_info || ""}</p>
                        </Section>
                        {isOwner && (
                            <Section title="Maintenance">
                                {passport.brand_contact_email && (
                                    <a
                                        href={`mailto:${passport.brand_contact_email}`}
                                        style={{ fontSize: 14, color: '#555' }}
                                    >
                                        Contact Merchant
                                    </a>
                                )}
                            </Section>
                        )}
                    </div>

                    {/* Slide 4: Ownership */}
                    <div className="passport-slide">
                        <Section title="Ownership">
                            <Row
                                label="Owner"
                                value={
                                    isOwner
                                        ? (passport.is_claimed ? "Me" : "Brand")
                                        : (passport.is_claimed ? "Individual" : "Brand")
                                }
                            />
                            <Row label="Owned since" value={passport.current_owner_since ? (() => { const d = new Date(passport.current_owner_since); const p = n => String(n).padStart(2,'0'); return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`; })() : null} />
                            {ownershipHistory.length > 0 && (
                                <Section title="History">
                                    {ownershipHistory.map((e, i) => {
                                        const label = e.transfer_type === 'initial_sale' ? 'Initial sale'
                                            : e.transfer_type?.replace(/_/g, ' ');
                                        const d = e.date ? new Date(e.date) : null;
                                        const p = n => String(n).padStart(2,'0');
                                        const date = d ? `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}` : null;
                                        return <Row key={i} label={label} value={date} />;
                                    })}
                                </Section>
                            )}
                        </Section>
                        {isOwner && (
                            <Section title="Estate Planning">
                            </Section>
                        )}
                        {!isOwner && !isBrand && (
                            <Section title="Inquiries">
                                {passport.brand_contact_email && (
                                    <a
                                        href={`mailto:${passport.brand_contact_email}`}
                                        style={{ fontSize: 14, color: '#555' }}
                                    >
                                        Contact Merchant
                                    </a>
                                )}
                            </Section>
                        )}
                        <Section title="QR Code">
                            <img
                                src={`${API_BASE_URL}/api/passport/${qrCodeId}/qr.png`}
                                alt="Product QR code"
                                style={{ width: 160, height: 160, display: 'block', margin: '8px auto' }}
                            />
                        </Section>
                        {passport.brand_logo_url && !logoFailed && (
                            <div className="passport-brand-footer">
                                <img
                                    className="passport-brand-logo"
                                    src={passport.brand_logo_url}
                                    alt={passport.brand_name}
                                    onError={() => setLogoFailed(true)}
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className="passport-dots">
                    {[0, 1, 2, 3].map(i => (
                        <button
                            key={i}
                            className={`passport-dot${activeSlide === i ? ' active' : ''}`}
                            onClick={() => scrollToSlide(i)}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

const fmtBool = (v) => v === true ? "Yes" : v === false ? "No" : null;

const RowAll = ({ label, value }) => (
    <div className="passport-row">
        <span className="passport-row-label">{label}</span>
        <span className="passport-row-value">
            {value !== null && value !== undefined && value !== "" ? String(value) : ""}
        </span>
    </div>
);

const BlockField = ({ label, value }) => (
    <div className="passport-block-field">
        <p className="passport-section-title" style={{ marginBottom: 4 }}>{label}</p>
        <p className="passport-description">{value || "—"}</p>
    </div>
);

const Section = ({ title, children }) => (
    <div className="passport-section">
        <h3 className="passport-section-title">{title}</h3>
        {children}
    </div>
);

// Only render rows that actually have a value, so the passport stays clean.
const Row = ({ label, value }) => {
    if (value === null || value === undefined || value === "") return null;
    return (
        <div className="passport-row">
            <span className="passport-row-label">{label}</span>
            <span className="passport-row-value">{String(value)}</span>
        </div>
    );
};

export default Passport;
